import { datadogRum } from '@datadog/browser-rum';
import React, { createContext, useContext, useEffect } from 'react';

export interface PageEventContextValue {
  pageName: string;
  pageSubName: string | null;
  sectionName: string;
}

const PageEventContext = createContext<PageEventContextValue | null>(null);

const PageEventDepthContext = createContext(0);

const scopeStack: { value: PageEventContextValue; depth: number }[] = [];

const getDeepestScope = () =>
  scopeStack.length === 0
    ? null
    : scopeStack.reduce((max, e) => (e.depth > max.depth ? e : max)).value;

const syncBodyScope = () => {
  if (typeof document === 'undefined') return;
  const deepest = getDeepestScope();
  if (deepest === null) {
    document.body.removeAttribute('data-analytics-scope');
    return;
  }
  document.body.setAttribute('data-analytics-scope', JSON.stringify(deepest));
};

const syncRumScope = () => {
  const deepest = getDeepestScope();
  if (deepest === null) {
    datadogRum.removeGlobalContextProperty('page');
    return;
  }
  datadogRum.setGlobalContextProperty('page', deepest);
};

const PageEventContextProvider: React.FC<
  React.PropsWithChildren<{ value: PageEventContextValue | null | undefined }>
> = ({ value, children }) => {
  const parentDepth = useContext(PageEventDepthContext);
  const myDepth = parentDepth + 1;

  useEffect(() => {
    if (!value) return;
    const entry = { value, depth: myDepth };
    scopeStack.push(entry);
    syncBodyScope();
    syncRumScope();
    return () => {
      const idx = scopeStack.indexOf(entry);
      if (idx >= 0) scopeStack.splice(idx, 1);
      syncBodyScope();
      syncRumScope();
    };
  }, [value, myDepth]);

  if (!value) return <>{children}</>;

  return (
    <PageEventDepthContext.Provider value={myDepth}>
      <PageEventContext.Provider value={value}>
        <div data-analytics-scope={JSON.stringify(value)}>{children}</div>
      </PageEventContext.Provider>
    </PageEventDepthContext.Provider>
  );
};

export default PageEventContext;
export { PageEventContextProvider };
